'use client';

import React, { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { useUIStore } from '@/store/uiStore';
import { useChatStore } from '@/store/chatStore';
import { useAuthStore } from '@/store/authStore';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

const CATEGORIES = [
  { value: 'ABUSE', label: 'Abuse', description: 'Physical or verbal abuse' },
  { value: 'HARASSMENT', label: 'Harassment', description: 'Persistent unwanted behavior' },
  {
    value: 'INAPPROPRIATE_CONTENT',
    label: 'Inappropriate Content',
    description: 'Sexual or disturbing content',
  },
  { value: 'SPAM', label: 'Spam', description: 'Repeated advertising or links' },
  { value: 'OTHER', label: 'Other', description: 'Other policy violations' },
];

export function ReportModal() {
  const { reportModalOpen, closeReportModal, addToast } = useUIStore();
  const { reportedUserId, roomId } = useChatStore();
  const { token } = useAuthStore();

  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit() {
    if (!category || !token) return;

    setLoading(true);
    try {
      await api.reports.create(
        {
          reportedUserId: reportedUserId || 'unknown',
          chatSessionId: roomId || undefined,
          category,
          description: description.trim() || undefined,
        },
        token
      );
      setSubmitted(true);
      addToast({ message: 'Report submitted. Thank you for keeping Stranger World safe.', type: 'success' });
      setTimeout(() => {
        closeReportModal();
        setSubmitted(false);
        setCategory('');
        setDescription('');
      }, 2000);
    } catch {
      addToast({ message: 'Failed to submit report. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      isOpen={reportModalOpen}
      onClose={closeReportModal}
      title="Report Stranger"
      size="md"
    >
      {submitted ? (
        <div className="text-center py-6">
          <div className="w-12 h-12 rounded-full bg-brand-lime/20 flex items-center justify-center mx-auto mb-4">
            <span className="text-brand-lime text-xl">✓</span>
          </div>
          <p className="text-brand-white font-medium">Report Submitted</p>
          <p className="text-sm text-brand-gray mt-1">Our team will review this report.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-brand-gray">
            Please select the reason for your report. All reports are reviewed by our moderation team.
          </p>

          {/* Category selection */}
          <div className="flex flex-col gap-2">
            {CATEGORIES.map(({ value, label, description: desc }) => (
              <button
                key={value}
                onClick={() => setCategory(value)}
                className={cn(
                  'flex items-start gap-3 p-3 rounded-xl border text-left transition-all duration-150',
                  category === value
                    ? 'border-brand-lime/50 bg-brand-lime/10'
                    : 'border-brand-gray-muted/50 hover:border-brand-gray hover:bg-brand-surface-3'
                )}
              >
                <div
                  className={cn(
                    'w-4 h-4 rounded-full border-2 mt-0.5 shrink-0 transition-colors',
                    category === value ? 'border-brand-lime bg-brand-lime' : 'border-brand-gray'
                  )}
                />
                <div>
                  <p className="text-sm font-medium text-brand-white">{label}</p>
                  <p className="text-xs text-brand-gray">{desc}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Optional description */}
          <Textarea
            label="Additional details (optional)"
            placeholder="Provide any additional context..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={500}
          />

          {!token && (
            <p className="text-xs text-yellow-400">
              You must be signed in to submit a report.
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="ghost" onClick={closeReportModal} fullWidth>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleSubmit}
              disabled={!category || !token}
              loading={loading}
              fullWidth
            >
              Submit Report
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
